if (!customElements.get('buyway-financing')) {
  customElements.define(
    'buyway-financing',
    class BuywayFinancing extends HTMLElement {
      connectedCallback() {
        this.messageElement = this.querySelector('[data-buyway-message]');
        this.summaryElement = this.querySelector('[data-buyway-summary]');
        this.durationRow = this.querySelector('[data-buyway-duration-row]');
        this.durationValue = this.querySelector('[data-buyway-duration-value]');
        this.rateRow = this.querySelector('[data-buyway-rate-row]');
        this.rateValue = this.querySelector('[data-buyway-rate-value]');
        this.monthlyPaymentValue = this.querySelector('[data-buyway-monthly-payment]');
        this.creditCostValue = this.querySelector('[data-buyway-credit-cost]');

        this.currencyFormatter = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: this.dataset.currency || 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        if (!this.dataset.endpoint) {
          this.renderUnavailable(this.dataset.unavailableText);
          return;
        }

        this.loadSimulation();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async loadSimulation() {
        this.renderLoading(this.dataset.loadingText);
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const response = await fetch(this.buildUrl(), {
            headers: {
              Accept: 'application/json',
            },
            signal: this.abortController.signal,
          });

          if (!response.ok) {
            if (response.status === 404) {
              this.renderUnavailable(this.dataset.unavailableText || this.dataset.errorText);
              return;
            }

            throw new Error(`Buy Way simulation request failed with status ${response.status}`);
          }

          const payload = await response.json();
          const simulation = this.normalizeSimulation(payload);

          if (simulation.monthlyPayment === null) {
            this.renderUnavailable(this.dataset.unavailableText);
            return;
          }

          this.renderSuccess(simulation);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.renderError(this.dataset.errorText);
        }
      }

      buildUrl() {
        const url = new URL(this.dataset.endpoint, window.location.origin);
        const amountInCents = Number.parseInt(this.dataset.amount || '0', 10);

        url.searchParams.set('amount', Number.isFinite(amountInCents) ? String(amountInCents) : '0');
        url.searchParams.set('amount_cents', Number.isFinite(amountInCents) ? String(amountInCents) : '0');
        url.searchParams.set(
          'amount_decimal',
          Number.isFinite(amountInCents) ? String((amountInCents / 100).toFixed(2)) : '0.00'
        );
        url.searchParams.set('currency', this.dataset.currency || 'EUR');
        url.searchParams.set('product_id', this.dataset.productId || '');
        url.searchParams.set('variant_id', this.dataset.variantId || '');
        return url.toString();
      }

      normalizeSimulation(payload) {
        const source = payload.offer || payload.simulation || payload.data || payload;

        return {
          monthlyPayment: this.asNumber(
            source.monthlyPayment ?? source.monthly_payment ?? source.monthlyAmount ?? source.monthly_amount
          ),
          durationMonths: this.asNumber(
            source.durationMonths ??
              source.duration_months ??
              source.duration ??
              source.numberOfPayments ??
              source.number_of_payments
          ),
          creditCost: this.asNumber(source.creditCost ?? source.credit_cost ?? source.totalCreditCost),
          debtorRate: this.asNumber(source.debtorRate ?? source.debtor_rate ?? source.rate),
          legalNoticeUrl:
            payload.legalNoticeUrl ||
            payload.legal_notice_url ||
            payload.legalNotices ||
            source.legalNoticeUrl ||
            source.legal_notice_url ||
            source.legalNotices ||
            '',
        };
      }

      asNumber(value) {
        if (value === null || value === undefined || value === '') return null;
        const normalized = Number.parseFloat(value);
        return Number.isFinite(normalized) ? normalized : null;
      }

      renderLoading(message) {
        this.dataset.state = 'loading';
        this.showMessage(message);
        this.summaryElement?.classList.add('hidden');
        this.durationRow?.classList.add('hidden');
        this.rateRow?.classList.add('hidden');
        this.updateLegalLink('');
      }

      renderUnavailable(message) {
        this.dataset.state = 'unavailable';
        this.showMessage(message);
        this.summaryElement?.classList.add('hidden');
        this.durationRow?.classList.add('hidden');
        this.rateRow?.classList.add('hidden');
        this.updateLegalLink('');
      }

      renderError(message) {
        this.dataset.state = 'error';
        this.showMessage(message);
        this.summaryElement?.classList.add('hidden');
        this.durationRow?.classList.add('hidden');
        this.rateRow?.classList.add('hidden');
        this.updateLegalLink('');
      }

      renderSuccess(simulation) {
        this.dataset.state = 'success';
        this.messageElement?.classList.add('hidden');
        this.summaryElement?.classList.remove('hidden');

        if (simulation.durationMonths !== null) {
          this.durationRow?.classList.remove('hidden');
          this.durationValue.textContent = simulation.durationMonths;
        } else {
          this.durationRow?.classList.add('hidden');
        }

        this.monthlyPaymentValue.textContent = this.currencyFormatter.format(simulation.monthlyPayment);

        if (simulation.creditCost !== null) {
          this.creditCostValue.textContent = this.currencyFormatter.format(simulation.creditCost);
        } else {
          this.creditCostValue.textContent = '-';
        }

        if (simulation.debtorRate !== null) {
          this.rateRow?.classList.remove('hidden');
          this.rateValue.textContent = `${simulation.debtorRate.toFixed(2)}%`;
        } else {
          this.rateRow?.classList.add('hidden');
        }

        this.updateLegalLink(simulation.legalNoticeUrl);
      }

      showMessage(message) {
        if (!this.messageElement) return;
        this.messageElement.textContent = message || '';
        this.messageElement.classList.remove('hidden');
      }

      updateLegalLink(url) {
        const modalLegalLink = document
          .getElementById(this.dataset.modalId)
          ?.querySelector('[data-buyway-legal-link]');

        if (!modalLegalLink) return;

        if (url) {
          modalLegalLink.href = url;
          modalLegalLink.classList.remove('hidden');
          return;
        }

        modalLegalLink.href = '#';
        modalLegalLink.classList.add('hidden');
      }
    }
  );
}